import { CollapsableCard } from '@app/app/_components/CollapsableCard/CollapsableCard';
import { CollapsablePanel } from '@app/app/_components/CollapsablePanel/CollapsablePanel';
import { ReadOnlyField } from '@app/app/_components/ReadOnlyField/ReadOnlyField';
import { FormState } from '@app/lib/form/contracts/FormState';
import { PurchaseTravelInsuranceResult } from '@app/service/eshop/travel/actions/purchaseTravelInsurance';
import { TravelInsuranceRequestResult } from '@app/service/eshop/travel/actions/requestTravelInsurance';
import { useTranslations } from 'next-intl';
import { format } from 'date-fns';
import { TextButton } from '@app/app/_components/TextButton/TextButton';
import React from 'react';
import { Paragraph } from '@app/app/_components/Paragraph/Paragraph';
import { CheckboxInput } from '@app/app/_components/Input/CheckboxInput';
import { ExpandableText } from '@app/app/_components/ExpandableText/ExpandableText';
import Link from 'next/link';
import { useAppParameters } from '@app/app/_context/AppParametersContext/useAppParameters';
import { PolicyholderSummaryCard } from '@app/app/esales/travel/_sections/steps/SummaryStep/PolicyholderSummaryCard';
import { TravelersSummaryCard } from '@app/app/esales/travel/_sections/steps/SummaryStep/TravelersSummaryCard';
import { PolicyholderFormFields } from '@app/app/esales/travel/_sections/TravelWizardSection/hooks/usePolicyholderFormFields';
import { InsuredTraveler } from '@app/domain/model/eshop/travel/InsuredTraveler';
import { PolicyPanel } from '@app/app/esales/travel/_sections/steps/SummaryStep/PolicyPanel';

interface Props {
    formState: FormState<PurchaseTravelInsuranceResult>;
    requestResult: Nullable<TravelInsuranceRequestResult>;
    policyholderFields: PolicyholderFormFields;
    isCompany: boolean;
    personalCode: string;
    onSavePolicyholder: (data: PolicyholderFormFields) => void;
    policyholderIsTraveler: boolean;
    insuredPersons: InsuredTraveler[];
    onChangeNumberOfPersons: () => void;
    editPolicyholderInfo: () => void;
    changeAgeOfTraveler: () => void;
    editTravelers: () => void;
    onSaveTravelers: (travelers: InsuredTraveler[]) => void;
    editTravelConditions: () => void;
    editPolicy: () => void;
}

export function SummaryStep({
    formState,
    requestResult,
    policyholderFields,
    isCompany,
    personalCode,
    onSavePolicyholder,
    policyholderIsTraveler,
    insuredPersons,
    onChangeNumberOfPersons,
    editPolicyholderInfo,
    changeAgeOfTraveler,
    editTravelers,
    onSaveTravelers,
    editTravelConditions,
    editPolicy,
}: Props) {
    const t = useTranslations();
    const parameters = useAppParameters();

    if (requestResult == null) {
        return null;
    }

    return (
        <div className="flex flex-col gap-4">
            <CollapsableCard defaultCollapsed={false} label={t('eshop.travel.summaryStep.travelInformation')}>
                <CollapsablePanel
                    addon={<TextButton onClick={editTravelConditions}>{t('general.edit')}</TextButton>}
                    collapsable={false}
                    label={t('eshop.travel.summaryStep.travelConditions')}>
                    <div className="grid w-full grid-cols-2 gap-y-4 border-b border-neutral-150 p-6">
                        <ReadOnlyField label={t('eshop.travel.summaryStep.startDate')}>
                            {format(requestResult.beginDate, 'yyyy-MM-dd')}
                        </ReadOnlyField>
                        <ReadOnlyField label={t('eshop.travel.summaryStep.endDate')}>
                            {format(requestResult.endDate, 'yyyy-MM-dd')}
                        </ReadOnlyField>
                        <ReadOnlyField label={t('eshop.travel.summaryStep.territory')}>
                            {requestResult.territory.name}
                        </ReadOnlyField>
                    </div>
                    <ReadOnlyField className="px-6 pt-6" label={t('eshop.travel.summaryStep.goalOfTheJourney')}>
                        {requestResult.goalOfTheJourney.name}
                    </ReadOnlyField>
                </CollapsablePanel>
            </CollapsableCard>

            <CollapsableCard defaultCollapsed={false} label={t('eshop.travel.summaryStep.travelers')}>
                <TravelersSummaryCard
                    insuredPersons={insuredPersons}
                    policyholderIsTraveler={policyholderIsTraveler}
                    formState={formState}
                    onSaveTravelers={onSaveTravelers}
                    onChangeNumberOfPersons={onChangeNumberOfPersons}
                    editPolicyholderInfo={editPolicyholderInfo}
                    changeAgeOfTraveler={changeAgeOfTraveler}
                    editTravelers={editTravelers}
                    isCompany={isCompany}
                />
            </CollapsableCard>

            <CollapsableCard defaultCollapsed={false} label={t('eshop.travel.summaryStep.policyholderInformation')}>
                <PolicyholderSummaryCard
                    policyholderFields={policyholderFields}
                    formState={formState}
                    isCompany={isCompany}
                    personalCode={personalCode}
                    onSave={onSavePolicyholder}
                />
            </CollapsableCard>

            <CollapsableCard defaultCollapsed={false} label={t('eshop.travel.summaryStep.policy')}>
                <PolicyPanel
                    selectedRisks={formState.data?.formData.selectedRisks || []}
                    editPolicy={editPolicy}
                    requestResult={requestResult}
                />
            </CollapsableCard>

            <div className="mt-6 flex flex-col gap-4">
                <CheckboxInput
                    name="acceptToPrivacyPolicy"
                    error={formState.violations?.acceptToPrivacyPolicy}
                    label={t.rich('eshop.travel.summaryStep.privacyPolicyConsentLabel', {
                        link: chunks => (
                            <Link className="underline" href={parameters.eshopFooterUrls.privacyPolicy} target="_blank">
                                {chunks}
                            </Link>
                        ),
                    })}
                />

                <CheckboxInput
                    name="acceptToTravelRules"
                    error={formState.violations?.acceptToTravelRules}
                    label={t.rich('eshop.travel.summaryStep.travelRulesConsentLabel', {
                        link: chunks => (
                            <Link
                                className="underline"
                                href={parameters.eshopTravelInfoUrls.travelInsuranceRules}
                                target="_blank">
                                {chunks}
                            </Link>
                        ),
                    })}
                />

                <ExpandableText className="text-gray-700 text-sm">
                    <div className="whitespace-pre-line">
                        {t.rich('eshop.travel.summaryStep.directMarketingInfoText', {
                            link: chunks => (
                                <Link
                                    className="underline"
                                    href={parameters.eshopFooterUrls.privacyPolicy}
                                    target="_blank">
                                    {chunks}
                                </Link>
                            ),
                        })}
                    </div>
                </ExpandableText>

                <div className="flex flex-col gap-4">
                    <Paragraph isBold>{t('eshop.travel.summaryStep.directMarketingAgreement')}</Paragraph>
                    <CheckboxInput
                        name="acceptToMarketingCnl"
                        label={t('eshop.travel.summaryStep.agreeToMarketingCompensa')}
                    />
                    <CheckboxInput
                        name="acceptToMarketingCl"
                        label={t('eshop.travel.summaryStep.agreeToMarketingCompensaLife')}
                    />
                </div>
            </div>
        </div>
    );
}
